import React, { useEffect, useRef, useState, useId, useMemo } from "react";
import { Icon } from "@amzn/awsui-components-react";
import ConsoleIcon from "@amzn/awsui-components-console/icon";
import useAgentSuggestions from "src/Ateam-BI/AutoMate/hooks/useAgentSuggestions";
import { APPS_LIBRARY_REPO_SEARCH_ENABLED } from "src/Ateam-BI/AutoMate/constants";
import "src/Ateam-BI/AutoMate/components/AISearchBar/AISearchBar.scss";

/**
 * Agent-backed search bar for the Apps Library.
 *
 * Renders the AutoMate-style pill input with live typeahead suggestions
 * powered by the existing `useAgentSuggestions` hook (autocompleteAgent via
 * AppSync with 1000ms debounce, 3-char minimum, request cancellation,
 * stale-response protection, deduplication, and LRU cache).
 *
 * On submit (Enter, the Search button, or picking a suggestion) it calls
 * `onSubmit(value)` so the parent can drive repository search or filtering
 * via the `searchQuery` URL param. The card collection is intentionally NOT
 * refreshed while the user is only typing.
 *
 * `isSearching` gates the duplicate-submission guard (Requirement 8.1): a
 * resubmission of the exact same query is only suppressed WHILE that query's
 * request is still in flight (rapid double-click protection). Once the
 * request has settled — success OR failure — resubmitting the identical
 * query is allowed again, so a failed search can be retried by clicking
 * Search / pressing Enter without having to change the text first.
 *
 * A "Clear AI Results" button renders beside Search — but only once an AI
 * search has actually been performed. That's derived from `initialValue`
 * (the `searchQuery` URL param the parent forwards here), NOT the live
 * `query` state — typing a new value without submitting must not reveal
 * the button, since there's nothing to clear yet. Clicking it clears the
 * input text and the autocomplete dropdown locally, then calls the optional
 * `onClear()` callback so the parent can restore the default (unsearched)
 * application collection and any UI it hid for the duration of the search
 * (e.g. a filter panel). `onClear` is a no-op omission-safe prop — parents
 * that don't pass it simply get local-only clearing.
 *
 * Requirements: 2.1-2.7, 6.5, 8.1
 */
function AppOpenSearchBar({ initialValue = "", placeholder = "Search all applications", onSubmit, onClear, isSearching = false }) {
  const [query, setQuery] = useState(initialValue);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const suggestionsRef = useRef(null);
  const isKeyboardNav = useRef(false);

  const instanceId = useId();
  const listboxId = `${instanceId}-suggestions-listbox`;
  const suggestionId = (index) => `${instanceId}-suggestion-${index}`;

  // "Clear AI Results" only makes sense once a search has actually been
  // submitted and is reflected in the URL (initialValue = searchQuery).
  // Deriving this from `initialValue` rather than the live `query` state
  // means typing a new value without submitting never reveals the button —
  // there'd be nothing to clear yet.
  const hasActiveSearch = initialValue.trim().length > 0;

  const {
    suggestions,
    isLoading: suggestionsLoading,
    onQueryChange: onSuggestQueryChange,
  } = useAgentSuggestions({ source: "repository" });

  // Show the "Searching..." indicator only when the backend is actively
  // fetching AND we have nothing to display yet.
  const showSearchingRow = suggestionsLoading && suggestions.length === 0;

  // Synchronously clamp the active index when suggestions shrink.
  const clampedActiveIndex = useMemo(
    () => (activeSuggestionIndex >= suggestions.length ? -1 : activeSuggestionIndex),
    [activeSuggestionIndex, suggestions.length]
  );

  // Synchronize internal input state when the URL-derived initial value
  // changes through browser navigation or reset (Requirement 6.5).
  useEffect(() => {
    setQuery(initialValue);
  }, [initialValue]);

  // Close the suggestions dropdown on outside click.
  useEffect(() => {
    const onDocClick = (e) => {
      // A null suggestionsRef means the dropdown isn't mounted (e.g. still
      // inside the debounce window before suggestions arrive) — treat that
      // as "click is not inside the dropdown" rather than short-circuiting
      // to false, otherwise showSuggestions never resets to false and the
      // dropdown can pop open later once suggestions arrive, even though
      // the user already clicked away.
      if (
        (!suggestionsRef.current || !suggestionsRef.current.contains(e.target)) &&
        wrapperRef.current && !wrapperRef.current.contains(e.target)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Scroll active suggestion into view during keyboard navigation.
  useEffect(() => {
    if (clampedActiveIndex >= 0) {
      const activeElement = document.getElementById(suggestionId(clampedActiveIndex));
      activeElement?.scrollIntoView({ block: "nearest" });
    }
  }, [clampedActiveIndex, instanceId]);

  const handleChange = (value) => {
    setQuery(value);
    setActiveSuggestionIndex(-1);
    setShowSuggestions(value.trim().length > 0);
    // Forward every input change to the autocomplete lifecycle. Autocomplete
    // is independent of the repository-submission switch (Req 2.1).
    onSuggestQueryChange(value);
  };

  const submit = (value) => {
    const trimmed = (value ?? query).trim();
    setShowSuggestions(false);
    setActiveSuggestionIndex(-1);
    // When the feature switch is disabled, suppress the submit callback
    // so no searchQuery URL param is set and no repository calls are made
    // (Req 10.4, 10.5). Empty submission (clear/reset) is always permitted.
    if (!APPS_LIBRARY_REPO_SEARCH_ENABLED && trimmed) return;
    // Prevent duplicate submission of the SAME query only WHILE it is still
    // in flight (Requirement 8.1) — rapid double-click / repeat-Enter
    // protection, not a permanent block. `initialValue` (the searchQuery URL
    // param) is set as soon as a search is dispatched and stays set after it
    // settles, whether it succeeded or failed; gating on `isSearching` (not
    // just value equality) ensures a FAILED search can be retried by
    // resubmitting the identical text once the request has settled — without
    // this, every retry of the same failed query would silently no-op.
    // Empty submission always proceeds as clear/reset.
    if (trimmed && isSearching && trimmed === initialValue.trim()) return;
    if (onSubmit) onSubmit(trimmed);
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion);
    submit(suggestion);
  };

  /**
   * "Clear AI Results" button handler. Distinct from an empty Enter/Search
   * submission: this always runs regardless of the repository-search
   * feature switch or the in-flight duplicate-submission guard, and it
   * notifies the parent via `onClear` (rather than `onSubmit('')`) so the
   * parent can restore the pre-search page state (default app collection,
   * filter panel visibility) in one dedicated path instead of overloading
   * the empty-submit branch of `onSubmit`.
   */
  const handleClearResults = () => {
    setQuery("");
    setShowSuggestions(false);
    setActiveSuggestionIndex(-1);
    // Clearing the input is also a real input change from the autocomplete
    // lifecycle's point of view — forward it so any in-flight suggestion
    // fetch is cancelled and the dropdown state resets consistently with
    // ordinary typing-to-empty.
    onSuggestQueryChange("");
    if (onClear) onClear();
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === "Enter") submit();
      if (e.key === "Escape") setShowSuggestions(false);
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        isKeyboardNav.current = true;
        setActiveSuggestionIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        isKeyboardNav.current = true;
        setActiveSuggestionIndex((prev) =>
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case "Escape":
        setShowSuggestions(false);
        setActiveSuggestionIndex(-1);
        break;
      case "Enter":
        if (clampedActiveIndex >= 0) {
          handleSuggestionClick(suggestions[clampedActiveIndex]);
        } else {
          submit();
        }
        break;
      default:
        break;
    }
  };

  const listboxHasContent = suggestions.length > 0 || showSearchingRow;
  const listboxOpen = showSuggestions && listboxHasContent;

  return (
    <div className="ai-search-container">
      <div className="ai-search-input-row">
        <div className="ai-search-wrapper" ref={wrapperRef}>
          <div className="ai-icon">
            <ConsoleIcon name="gen-ai" />
          </div>
          <input
            type="text"
            role="combobox"
            aria-autocomplete="list"
            aria-controls={listboxId}
            aria-expanded={listboxOpen}
            aria-haspopup="listbox"
            aria-activedescendant={clampedActiveIndex >= 0 ? suggestionId(clampedActiveIndex) : undefined}
            value={query}
            onChange={(e) => handleChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (query.trim().length > 0 && listboxHasContent) setShowSuggestions(true);
            }}
            placeholder={placeholder}
            className="ai-search-input"
          />
          {listboxOpen && (
            <div
              ref={suggestionsRef}
              className="ai-suggestions-dropdown"
              role="listbox"
              id={listboxId}
              onMouseMove={() => { isKeyboardNav.current = false; }}
            >
              {showSearchingRow ? (
                <div
                  role="option"
                  aria-disabled="true"
                  aria-selected="false"
                  className="ai-suggestion-item"
                >
                  <span className="ai-suggestion-icon"><Icon name="status-in-progress" /></span>
                  Searching…
                </div>
              ) : (
                suggestions.map((suggestion, index) => (
                  <div
                    key={`${suggestion}-${index}`}
                    id={suggestionId(index)}
                    role="option"
                    aria-selected={index === clampedActiveIndex}
                    className={`ai-suggestion-item${index === clampedActiveIndex ? " ai-suggestion-active" : ""}`}
                    onMouseDown={() => handleSuggestionClick(suggestion)}
                    onMouseEnter={() => { if (!isKeyboardNav.current) setActiveSuggestionIndex(index); }}
                    onMouseLeave={() => { if (!isKeyboardNav.current) setActiveSuggestionIndex(-1); }}
                  >
                    <span className="ai-suggestion-icon"><Icon name="search" /></span>
                    {suggestion}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
        <div className="ai-button-container">
          <button onClick={() => submit()} className="ai-ask-button">
            Search
          </button>
          {hasActiveSearch && (
            <button
              type="button"
              onClick={handleClearResults}
              className="ai-clear-button"
            >
              Clear AI Results
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default AppOpenSearchBar;
